import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v4 } from 'uuid';
import { z } from 'zod';
import { Files, Link2Off, UsersRound } from 'lucide-react';
import {
  parseResourceType,
  type ResourceShareLinkProblem,
  ResourceType,
  shareLinkProblemSchema,
} from '@cotera/api';
import { Assert } from '@cotera/utilities';
import { useTenantedClient, useWhoami } from '../../stores/org';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Loading, Text } from '@cotera/client/app/components/ui';
import { CocoEvent, useAnalytics } from '@cotera/client/app/etc';
import type {
  ActivateShareLinkOutput,
  ResourceSharePreview,
} from '@cotera/client/app/etc/api/typed-client';
import { ArtifactIcon } from '@cotera/client/v0/chat/artifact-snippet';
import { MessageSquareIcon } from 'lucide-react';
import { setPendingChatSubmission } from '../chat/models/pending-chat-submission';
import { queryKeys } from '../../hooks/query-cache-keys';
import { resourceItemPath } from '../resources/navigation/resource-navigator';
import { shareTargetLabel } from '../resources/share/share-targets';
import { apiErrorMessage } from '../resources/share/use-share-access';
import {
  buildChatSeedSubmission,
  resolveChatSeedRef,
} from './share-link-chat-seed';

const postActivateStepSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('navigate'),
    path: z.string().min(1),
  }),
  z.object({
    t: z.literal('chat'),
    prompt: z.string(),
  }),
]);

const postActivateSchema = z.object({
  steps: z.array(postActivateStepSchema),
});

type PostActivateStep = z.infer<typeof postActivateStepSchema>;

type ShareLinkProblem = ResourceShareLinkProblem;

const isShareLinkProblem = (
  code: string | undefined
): code is ShareLinkProblem => shareLinkProblemSchema.safeParse(code).success;

/**
 * Where to land after activating a share link. The target comes from a stored
 * share link, so its type is only a string until {@link parseResourceType}
 * vouches for it; an unrecognised one falls back to the org root rather than
 * building a path out of it.
 */
function getDefaultTargetPath(
  orgSlug: string,
  target: { type: ResourceType | string; id: string }
): string {
  const type = parseResourceType(target.type);
  return type === null
    ? `/${orgSlug}`
    : resourceItemPath(orgSlug, type, target.id);
}

function parsePostActivateSteps(
  postActivationSequence:
    | {
        steps: PostActivateStep[];
      }
    | null
    | undefined
): PostActivateStep[] {
  if (postActivationSequence === undefined || postActivationSequence === null) {
    return [];
  }
  const parsed = postActivateSchema.safeParse(postActivationSequence);
  if (parsed.success === false) {
    return [];
  }
  return parsed.data.steps;
}

function toOrgPath(path: string, orgSlug: string): string {
  if (path.startsWith(`/${orgSlug}/`)) {
    return path;
  }
  if (path.startsWith('/')) {
    return `/${orgSlug}${path}`;
  }
  return `/${orgSlug}/${path}`;
}

const sharerName = (
  sharedBy: { name: string | null; email: string | null } | null
) => sharedBy?.name ?? sharedBy?.email ?? 'Someone';

/** What went wrong, in words the person holding the link can act on. */
function describeProblem(
  problem: ShareLinkProblem,
  sharedBy: { name: string | null; email: string | null } | null,
  signedInAs: string
): { title: string; body: string } {
  const who = sharerName(sharedBy);
  const askForNew =
    sharedBy === null ? 'Ask for a new link.' : `Ask ${who} for a new one.`;
  switch (problem) {
    case 'not_found':
      return {
        title: "This link doesn't work",
        body: 'Check that you copied the whole link.',
      };
    case 'revoked':
      return { title: 'This link was turned off', body: askForNew };
    case 'expired':
      return { title: 'This link has expired', body: askForNew };
    case 'used':
      return { title: 'This link was already used', body: askForNew };
    case 'wrong_org':
      return {
        title: 'This link is for another organization',
        body: "It only works for members of the organization it was shared from. If that's you, switch organizations and open it again.",
      };
    case 'wrong_person':
      // The invited address isn't shown: whoever holds a forwarded link
      // shouldn't learn who it was meant for.
      return {
        title: 'This link is for a different email',
        body: `You're signed in as ${signedInAs}. Sign in with the address it was sent to, or ask ${
          sharedBy === null ? 'whoever sent it' : who
        } to add you.`,
      };
    case 'sharer_lost_access':
      return {
        title: 'This link no longer works',
        body: `${who} can't share this anymore. Ask someone who can edit it for a new link.`,
      };
    case 'workflow_cross_org':
      return {
        title: "Workflows can't be shared across organizations",
        body: `This includes a workflow, which only crosses organizations as a copy. Ask ${who} to send you a copy instead.`,
      };
    case 'no_targets':
      return { title: 'Nothing to open', body: askForNew };
    default:
      return Assert.unreachable(problem);
  }
}

/**
 * Activates a link and lands on what it opened: the original for an access
 * link, the new copy for a copy link, or a seeded chat when the link says so.
 */
function useActivateShareLink(token: string) {
  const client = useTenantedClient();
  const navigate = useNavigate();
  const org = useWhoami((s) => s.org);
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const [state, setState] = useState<
    | { status: 'idle' }
    | { status: 'activating' }
    | { status: 'failed'; message: string; problem: ShareLinkProblem | null }
  >({ status: 'idle' });

  const land = useCallback(
    async (output: ActivateShareLinkOutput) => {
      const target =
        output.copiedResources !== null && output.copiedResources.length > 0
          ? output.copiedResources[0]!
          : output.copiedResource !== null
          ? output.copiedResource
          : output.targets[0] !== undefined
          ? {
              type: output.targets[0].type,
              id: output.targets[0].id,
            }
          : {
              type: output.targetResourceType,
              id: output.targetResourceId,
            };
      // Invalidate cached queries for any newly-copied resources so the
      // destination page doesn't render a "not found" flash from a stale
      // cache before the websocket-driven invalidation lands — and for the
      // originals, since an access link changes the permission they carry.
      const touched = [...(output.copiedResources ?? []), ...output.targets];
      const invalidations: Promise<unknown>[] = [];
      for (const resource of touched) {
        const type = parseResourceType(resource.type);
        if (type === null) {
          continue;
        }
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: queryKeys.resource.get({
              orgId: org.id,
              type,
              id: resource.id,
            }),
          })
        );
      }
      // Lists / shell directory may now include the copied resources
      invalidations.push(
        queryClient.invalidateQueries({
          queryKey: [org.id, 'resource', 'list'],
        }),
        queryClient.invalidateQueries({
          queryKey: [org.id, 'resource', 'listPage'],
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.resource.folderTree({ orgId: org.id }),
        }),
        // Agents shell directory — backs useAgentsDisplayNames /
        // useAgentsIdByKey, used by chat input headers to resolve a copied
        // agent's title. Without this, focus_agent finds the agent but the
        // chat renders a generic "Agent" label instead of the real name.
        queryClient.invalidateQueries({
          queryKey: queryKeys.agents.all({ orgId: org.id }),
        })
      );
      await Promise.all(invalidations);

      const steps = parsePostActivateSteps(output.postActivationSequence);
      let nextPath = getDefaultTargetPath(org.slug, target);
      let chatPrompt: string | null = null;
      for (const step of steps) {
        if (step.t === 'navigate') {
          nextPath = toOrgPath(step.path, org.slug);
          continue;
        }
        if (step.t === 'chat') {
          chatPrompt = step.prompt;
        }
      }

      if (chatPrompt !== null) {
        const chatId = v4();
        setPendingChatSubmission(chatId, {
          text: chatPrompt,
          ...buildChatSeedSubmission(resolveChatSeedRef(nextPath, target)),
        });
        navigate(`/${org.slug}/chats/${chatId}`, {
          replace: true,
          state: { entrypoint: 'shared_link' },
        });
        return;
      }
      navigate(nextPath, {
        replace: true,
        state: { entrypoint: 'shared_link' },
      });
    },
    [navigate, org.id, org.slug, queryClient]
  );

  const activate = useCallback(async () => {
    setState({ status: 'activating' });
    const result = await client.resource.activateShareLink({ token });
    if (result.isErr()) {
      // Share endpoints answer without the { t, err } envelope, so the code
      // arrives on a `server` error as often as on an `application` one.
      const code = result.error.err.code;
      setState({
        status: 'failed',
        message: apiErrorMessage(result.error, "Couldn't open this link"),
        problem: isShareLinkProblem(code) ? code : null,
      });
      return;
    }
    analytics.track(CocoEvent.ShareLinkOpened, {
      shareMode: result.value.mode,
      targetCount: result.value.targets.length,
    });
    await land(result.value);
  }, [analytics, client.resource, land, token]);

  return { state, activate };
}

export const ShareLinkActivatePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  if (token === undefined || token === '') {
    return (
      <ShareProblem
        title="This link doesn't work"
        body="Check that you copied the whole link."
      />
    );
  }
  return <ShareLinkLanding token={token} />;
};

/**
 * Before anything is granted or copied, say what the link is: who shared what,
 * and what opening it will do. Same-org access links go straight through, as
 * a Docs link does — there is nothing to decide. A copy (which puts new things
 * in your workspace) or access to another org's work waits for a click.
 */
const ShareLinkLanding: React.FC<{ token: string }> = ({ token }) => {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  const orgSlug = useWhoami((s) => s.org.slug);
  const signedInAs = useWhoami((s) => s.whoami.email);
  const navigate = useNavigate();
  const { state, activate } = useActivateShareLink(token);
  const autoActivatedRef = useRef(false);

  const previewQuery = useQuery({
    queryKey: [orgId, 'resource', 'sharePreview', token],
    queryFn: async () => {
      const result = await client.resource.previewShareLink({ token });
      if (result.isErr()) {
        throw new Error(
          apiErrorMessage(result.error, "Couldn't read this link")
        );
      }
      return result.value;
    },
    retry: false,
    staleTime: Infinity,
  });
  const preview = previewQuery.data;
  const goesStraightThrough =
    preview?.status === 'ready' &&
    preview.shareMode === 'access' &&
    preview.sameOrg;

  useEffect(() => {
    if (!goesStraightThrough || autoActivatedRef.current) {
      return;
    }
    // In React StrictMode (dev), effects mount/unmount/remount once. The ref
    // survives that, so the link is activated once rather than twice.
    autoActivatedRef.current = true;
    void activate();
  }, [activate, goesStraightThrough]);

  const goHome = () => navigate(`/${orgSlug}/chats`, { replace: true });

  if (state.status === 'failed') {
    const described =
      state.problem !== null
        ? describeProblem(state.problem, null, signedInAs)
        : null;
    return (
      <ShareProblem
        title={described?.title ?? "Couldn't open this link"}
        body={described?.body ?? state.message}
        onRetry={described === null ? () => void activate() : undefined}
        onHome={goHome}
      />
    );
  }

  if (
    previewQuery.isPending ||
    goesStraightThrough ||
    state.status === 'activating'
  ) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3">
        <Loading.Spinner variant="sm" />
        <Text.P className="text-sm text-muted-foreground">
          Opening shared link…
        </Text.P>
      </div>
    );
  }

  if (preview === undefined) {
    return (
      <ShareProblem
        title="Couldn't open this link"
        body={previewQuery.error?.message ?? 'Try again in a moment.'}
        onRetry={() => void previewQuery.refetch()}
        onHome={goHome}
      />
    );
  }

  if (preview.status === 'unavailable') {
    const { title, body } = describeProblem(
      preview.problem,
      preview.sharedBy,
      signedInAs
    );
    return <ShareProblem title={title} body={body} onHome={goHome} />;
  }

  return (
    <ShareConfirm
      preview={preview}
      onConfirm={() => void activate()}
      onCancel={goHome}
    />
  );
};

const ShareConfirm: React.FC<{
  preview: Extract<ResourceSharePreview, { status: 'ready' }>;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ preview, onConfirm, onCancel }) => {
  const who = sharerName(preview.sharedBy);
  const [first] = preview.targets;
  const what =
    preview.targets.length === 1 && first !== undefined
      ? `"${first.title}"`
      : `${preview.targets.length} items`;
  const isCopy = preview.shareMode === 'copy';
  const fromOrg =
    !preview.sameOrg && preview.sourceOrgName !== null
      ? ` at ${preview.sourceOrgName}`
      : '';

  return (
    <div className="flex h-full w-full items-center justify-center p-6">
      <div className="flex w-full max-w-md flex-col gap-5 rounded-lg border border-border bg-background p-6 shadow-sm">
        <span
          className={
            isCopy
              ? 'flex h-10 w-10 items-center justify-center rounded-full bg-violet-500/15 text-violet-700 dark:text-violet-300'
              : 'flex h-10 w-10 items-center justify-center rounded-full bg-sky-500/15 text-sky-700 dark:text-sky-300'
          }
        >
          {isCopy ? (
            <Files className="h-5 w-5" />
          ) : (
            <UsersRound className="h-5 w-5" />
          )}
        </span>
        <div className="flex flex-col gap-1">
          <Text.P className="text-base font-medium text-foreground">
            {isCopy
              ? `${who}${fromOrg} sent you a copy of ${what}`
              : `${who}${fromOrg} shared ${what} with you`}
          </Text.P>
          <Text.P className="text-sm text-muted-foreground">
            {isCopy
              ? "You'll get your own copy in your workspace. Changes you make stay with you and don't affect the original."
              : `You'll be able to ${
                  preview.permission === 'view' ? 'view' : 'view and edit'
                } the original, alongside the people already working on it.`}
          </Text.P>
        </div>
        {preview.targets.length > 1 ? (
          <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-md border border-border p-2">
            {preview.targets.map((target) => (
              <li
                key={`${target.type}:${target.id}`}
                className="flex items-center gap-2 px-1 py-0.5 text-sm"
              >
                {target.type === ResourceType.Chat ? (
                  <MessageSquareIcon className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ArtifactIcon
                    artifactType={target.type}
                    className="h-4 w-4"
                  />
                )}
                <span className="min-w-0 flex-1 truncate">{target.title}</span>
                <span className="text-xs text-muted-foreground">
                  {shareTargetLabel(target.type)}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            Not now
          </Button>
          <Button onClick={onConfirm}>
            {isCopy
              ? 'Make a copy'
              : preview.alreadyHasAccess
              ? 'Open'
              : 'Open and join'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ShareProblem: React.FC<{
  title: string;
  body: string;
  onRetry?: () => void;
  onHome?: () => void;
}> = ({ title, body, onRetry, onHome }) => (
  <div className="flex h-full w-full items-center justify-center p-6">
    <div className="flex max-w-sm flex-col items-center gap-3 text-center">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Link2Off className="h-5 w-5" />
      </span>
      <Text.P className="text-sm font-medium text-foreground">{title}</Text.P>
      <Text.P className="text-sm text-muted-foreground">{body}</Text.P>
      <div className="flex gap-2 pt-2">
        {onRetry !== undefined ? (
          <Button variant="outline" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
        {onHome !== undefined ? (
          <Button onClick={onHome}>Go to Coco</Button>
        ) : null}
      </div>
    </div>
  </div>
);
